import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronsLeftRight, Phone } from "lucide-react";
import Nav from "./Nav";
import Footer from "./Footer";
import { Reveal } from "./Reveal";

const PAIRS = [
  {
    id: "brick-estate",
    title: "White Brick Estate",
    scope: "Rough cut & rock piles → laser finish grade",
    before: "/ba-d.jpg",
    after: "/ba-e.jpg",
    beforeAlt: "Rough-cut red clay with rock piles behind a white brick home",
    afterAlt: "Same yard finish-graded into smooth seeded rows",
  },
  {
    id: "hilltop-modern",
    title: "Hilltop Modern Home",
    scope: "Rough grade → fresh hydroseed application",
    before: "/ba-a.jpg",
    after: "/ba-c.jpg",
    beforeAlt: "Rough-graded dirt with machine tracks around a modern hilltop home",
    afterAlt: "Same slope covered in fresh teal hydroseed slurry",
  },
  {
    id: "family-backyard",
    title: "Family Backyard Revival",
    scope: "Worn-out dirt yard → hydroseeded green",
    before: "/p-11.jpg",
    after: "/p-12.jpg",
    beforeAlt: "Bare worn-out backyard with a wooden playscape and white fence",
    afterAlt: "Same backyard covered in fresh green hydroseed",
  },
  {
    id: "gray-cottage",
    title: "Gray Cottage Lot",
    scope: "Bare winter dirt → thick summer lawn",
    before: "/p-07.jpg",
    after: "/p-09.jpg",
    beforeAlt: "Bare dirt and patchy grass around a gray cottage with a deck",
    afterAlt: "Same cottage surrounded by a thick green lawn and new driveway",
  },
  {
    id: "shuttered-homestead",
    title: "Shuttered Homestead",
    scope: "Fresh-graded dirt → established lawn",
    before: "/p-02.jpg",
    after: "/p-06.webp",
    beforeAlt: "Rough brown graded dirt in front of a white farmhouse with black shutters",
    afterAlt: "Same farmhouse with a full green lawn grown in",
  },
  {
    id: "barn-hill",
    title: "Modern Barn Hill",
    scope: "Spray day → grown in thick",
    before: "/p-01.jpg",
    after: "/p-04.jpg",
    beforeAlt: "Fresh teal hydroseed on a hill below a modern dark barn-style home",
    afterAlt: "Same hill grown into a tall, dense green lawn",
  },
];

const CompareSlider = ({ pair, index }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const move = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <Reveal delay={0.08 * index}>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <h2
          data-testid={`ba-title-${pair.id}`}
          className="font-display text-2xl font-bold tracking-tight text-[#1a2e22] sm:text-3xl"
        >
          {pair.title}
        </h2>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#2e7d4f]">
          {pair.scope}
        </p>
      </div>
      <div
        ref={ref}
        data-testid={`ba-slider-${pair.id}`}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          move(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) move(e.clientX);
        }}
        className="relative aspect-[4/3] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-md border border-[#1c4431]/20 sm:aspect-[16/9]"
      >
        <img
          src={pair.before}
          alt={pair.beforeAlt}
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
        >
          <img
            src={pair.after}
            alt={pair.afterAlt}
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <span className="absolute left-4 top-4 rounded-full bg-[#122e21]/80 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#f6f1e3] backdrop-blur-sm">
          Before
        </span>
        <span className="absolute right-4 top-4 rounded-full bg-[#1f5c3d]/90 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#f6f1e3] backdrop-blur-sm">
          After
        </span>
        <div
          className="absolute inset-y-0 z-10 w-0.5 bg-[#f6f1e3] shadow-[0_0_20px_rgba(18,46,33,0.6)]"
          style={{ left: `${pos}%` }}
        >
          <span
            data-testid={`ba-handle-${pair.id}`}
            className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-[#f6f1e3] bg-[#1f5c3d] text-[#f6f1e3] shadow-xl"
          >
            <ChevronsLeftRight className="h-5 w-5" />
          </span>
        </div>
      </div>
    </Reveal>
  );
};

export default function BeforeAfter() {
  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Nav />
      <main
        data-testid="before-after-page"
        className="mx-auto max-w-7xl px-4 pb-24 pt-36 sm:px-8 md:pt-44 lg:px-16"
      >
        <Reveal>
          <Link
            to="/"
            data-testid="ba-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#2e7d4f] transition-colors duration-300 hover:text-[#1a2e22]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to the main page
          </Link>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-10 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#2e7d4f] sm:text-sm">
            Before / After
          </p>
          <h1
            data-testid="ba-heading"
            className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#1a2e22] sm:text-6xl lg:text-7xl"
          >
            Drag the line.
          </h1>
          <p
            data-testid="ba-subcopy"
            className="mt-6 max-w-xl text-base leading-relaxed text-[#5c6b58] sm:text-lg"
          >
            Every pair below is the same property — shot before we touched it
            and after we finished. No stock photos, no other crew's work.
          </p>
        </Reveal>

        <div className="mt-16 space-y-20 md:mt-24">
          {PAIRS.map((pair, i) => (
            <CompareSlider key={pair.id} pair={pair} index={i} />
          ))}
        </div>

        <Reveal>
          <p
            data-testid="recent-work-overline"
            className="mt-28 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#2e7d4f] sm:text-sm"
          >
            Recent work — straight off the truck
          </p>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { src: "/work2.jpg", caption: "Spray day — new build slope", testid: "recent-work-1" },
            { src: "/work4.jpg", caption: "Finish grade — hilltop modern", testid: "recent-work-2" },
            { src: "/p-03.jpg", caption: "Fresh application — shuttered homestead", testid: "recent-work-3" },
            { src: "/p-08.jpg", caption: "Spray pass — gray cottage", testid: "recent-work-4" },
            { src: "/p-10.jpg", caption: "Spring fill-in — gray cottage", testid: "recent-work-5" },
            { src: "/p-05.jpg", caption: "Crew on site — lawn coming in", testid: "recent-work-6" },
          ].map((p, i) => (
            <Reveal key={p.testid} delay={0.08 * i}>
              <figure data-testid={p.testid} className="group">
                <div className="overflow-hidden rounded-md border border-[#1c4431]/20">
                  <img
                    src={p.src}
                    alt={p.caption}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <figcaption className="mt-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1f5c3d]">
                  {p.caption}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-24 flex flex-wrap items-center justify-between gap-6 rounded-md border border-[#122e21] bg-[#16382a] p-8 md:p-12">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9fd8b4]">
                Your property next?
              </p>
              <p className="mt-3 font-display text-2xl font-bold tracking-tight text-[#f6f1e3] sm:text-3xl">
                On-site pricing. Straight numbers.
              </p>
            </div>
            <a
              data-testid="ba-call-button"
              href="tel:+14237482012"
              className="flex items-center gap-3 rounded-full bg-[#f6f1e3] px-8 py-4 font-mono text-sm font-bold uppercase tracking-widest text-[#1f5c3d] transition-colors duration-300 hover:bg-white"
            >
              <Phone className="h-4 w-4" />
              Call 423-748-2012
            </a>
          </div>
        </Reveal>
      </main>
      <Footer />
    </>
  );
}
